/**
 * JWT関連ユーティリティ関数
 * トークンのデコード、有効期限チェックなどを提供
 */
/**
 * JWTトークンをデコードしてペイロードを取得
 *
 * @param token - JWT文字列（"header.payload.signature"形式）
 * @returns デコードされたペイロード
 * @throws JWT形式が不正、またはBase64/JSONデコードに失敗した場合
 */
export function decodeJwt(token) {
    const parts = token.split(".");
    if (parts.length !== 3) {
        throw new Error('Invalid JWT format: expected 3 parts separated by "."');
    }
    const payloadPart = parts[1];
    if (!payloadPart) {
        throw new Error("Invalid JWT format: payload part is missing");
    }
    try {
        // URL-safe Base64をBase64に変換（- → +、_ → /）
        const base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
        // Base64デコード（ブラウザ環境とNode.js環境の両方に対応）
        let decoded;
        if (typeof atob === "function") {
            // ブラウザ環境
            decoded = atob(base64);
        }
        else if (typeof Buffer !== "undefined") {
            // Node.js環境
            decoded = Buffer.from(base64, "base64").toString("utf-8");
        }
        else {
            throw new Error("Base64 decoding is not supported in this environment");
        }
        // JSONパース
        const payload = JSON.parse(decoded);
        return payload;
    }
    catch (error) {
        if (error instanceof Error) {
            throw new Error(`Failed to decode JWT: ${error.message}`);
        }
        throw new Error("Failed to decode JWT: unknown error");
    }
}
/**
 * JWTトークンが有効期限切れかどうかをチェック
 *
 * @param token - JWT文字列
 * @returns true: 期限切れ、false: 有効
 */
export function isTokenExpired(token) {
    try {
        const payload = decodeJwt(token);
        // exp クレームが存在しない場合は期限切れとみなす
        if (typeof payload.exp !== "number") {
            return true;
        }
        const now = Math.floor(Date.now() / 1000); // 現在時刻（Unix timestamp秒）
        return payload.exp <= now; // 期限が現在時刻以下なら期限切れ
    }
    catch {
        // デコードエラー時は期限切れとみなす
        return true;
    }
}
/**
 * JWTトークンの有効期限までの残り秒数を取得
 *
 * @param token - JWT文字列
 * @returns 残り秒数（負の値の場合は0を返す）
 */
export function getTokenExpiresIn(token) {
    try {
        const payload = decodeJwt(token);
        // exp クレームが存在しない場合は0を返す
        if (typeof payload.exp !== "number") {
            return 0;
        }
        const now = Math.floor(Date.now() / 1000); // 現在時刻（Unix timestamp秒）
        const expiresIn = payload.exp - now;
        // 負の値（既に期限切れ）の場合は0を返す
        return Math.max(0, expiresIn);
    }
    catch {
        // デコードエラー時は0を返す
        return 0;
    }
}
//# sourceMappingURL=jwt.js.map